/**
 * @file THE FRUSTUM, AS SIX PLANES AND NO THREE.JS.
 *
 * This module exists because of one measurement. The game's camera is a 30°
 * lens at a 52° pitch, 34 m out (90 m at full zoom), and the ground it can
 * actually see is a **38 m x 38 m** patch — about 1.4% of the 320 m city, or
 * ~10% at maximum zoom-out. Everything the fleet and the prop batches submit
 * beyond that patch is work with no pixel behind it.
 *
 * Three.js already frustum-culls, so the obvious question is why this exists.
 * The answer is that its test is PER OBJECT, and every batch in this engine is
 * one object holding instances spread over the whole map:
 *
 *   • `city.wild.rock`   98 instances, bounding sphere radius 389 m
 *   • `city.roof.vent`  402 instances, bounding sphere radius 259 m
 *   • a `CarFleet` archetype batch — cars parked in four different districts
 *
 * The camera stands INSIDE every one of those spheres, so the per-object test
 * passes every frame and rejects nothing. Measured on the shipped seed: 99.8%
 * of prop instances and 99.9% of car part instances were outside the frustum
 * while all of them were being drawn. The fix is a test at INSTANCE
 * granularity, which is what this file is the primitive for.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IMPORTS NOTHING
 * ─────────────────────────────────────────────────────────────────────────────
 * `setFromViewProjection` takes 16 plain numbers — `camera.projectionMatrix ·
 * camera.matrixWorldInverse`, in three.js's COLUMN-MAJOR element order — and
 * not a `THREE.Camera`. That is the same discipline `traffic/Traffic.js` and
 * `systems/Driving.js` keep: no three.js import means the whole thing is
 * provable in node, which is what `tests/culling.test.mjs` does.
 *
 * ⚠ The plane extraction below is the Gribb-Hartmann derivation against
 * three.js's column-major layout, where the element at (row, col) is
 * `e[row + 4 * col]` — so the w-row is `e[3], e[7], e[11], e[15]`. Get that
 * transposed and the frustum is a plausible-looking box pointing somewhere
 * else entirely, which culls the world at the screen edges and looks like a
 * z-fighting bug. It was verified against a live camera by projecting the
 * player to NDC (0.000, −0.003) before it was written down.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DEPTH CONVENTION IS AN ARGUMENT, NOT AN ASSUMPTION
 * ─────────────────────────────────────────────────────────────────────────────
 * The four SIDE planes are the same under either graphics API. The near and far
 * planes are not, and this project can present both:
 *
 *   WebGL   clip z in −w..w  ->  near = row3 + row2,  far = row3 − row2
 *   WebGPU  clip z in  0..w  ->  near = row2,         far = row3 − row2
 *
 * `WebGPURenderer` stamps its own `coordinateSystem` onto the camera and
 * rebuilds the projection during `render()`, so a camera that has never been
 * rendered still reports the WebGL default — which is exactly how this was
 * found: the live renderer said `coordinateSystem: 2001` (WebGPU) while
 * `camera.coordinateSystem` still said 2000.
 *
 * So {@link Frustum.setFromViewProjection} takes `zeroToOne` and the CALLER
 * reads `camera.coordinateSystem === THREE.WebGPUCoordinateSystem`, which is
 * the value that is true at the moment the matrix was built.
 *
 * ⚠ Do NOT over-rate this flag. Measured, getting it backwards moves the near
 * plane between 0.25 m and 1.0 m from the eye instead of 0.5 m — a sub-metre
 * shell around the camera and nothing else; the four side planes and the far
 * plane are bit-identical either way. This camera sits 34 m from the action,
 * so no instance is ever inside that shell and the mismatch culls exactly
 * nothing differently. It is wired up because it is free and correct, not
 * because it was a bug — if you are chasing missing geometry, the cause is
 * somewhere else and this is the wrong thread to pull.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PLANES ARE NORMALISED, DELIBERATELY
 * ─────────────────────────────────────────────────────────────────────────────
 * Each plane is divided through by its normal's length at extraction time, so
 * `sphereVisible` can compare a signed distance against a RADIUS in metres.
 * Skipping the normalisation makes the test scale-dependent and quietly wrong
 * for big instances (a tree survives, a vent does not). It is six square roots
 * per camera update against thousands of instance tests, so it is free.
 *
 * @module engine/render/Culling
 */

/** Plane order, for anyone debugging a rejection. */
export const PLANE = Object.freeze({
  RIGHT: 0,
  LEFT: 1,
  BOTTOM: 2,
  TOP: 3,
  FAR: 4,
  NEAR: 5,
});

export class Frustum {
  constructor() {
    /**
     * Six planes as `(nx, ny, nz, d)`, normalised. A point is inside when
     * `n · p + d >= 0` for all six.
     * @type {Float64Array}
     */
    this.planes = new Float64Array(24);
    /** Bumped on every successful `setFromViewProjection`. */
    this.epoch = 0;
  }

  /**
   * @param {ArrayLike<number>} e A 16-element COLUMN-MAJOR view-projection
   *   matrix: `camera.projectionMatrix.clone().multiply(camera.matrixWorldInverse).elements`.
   * @param {boolean} [zeroToOne=true] True when clip Z runs 0..w (WebGPU),
   *   false when it runs −w..w (WebGL). Read it from the CAMERA
   *   (`camera.coordinateSystem === WebGPUCoordinateSystem`), never from a
   *   constant — see the depth-convention note in the file header.
   * @returns {Frustum} this
   */
  setFromViewProjection(e, zeroToOne = true) {
    const p = this.planes;
    // Row r of the matrix is (e[r], e[r+4], e[r+8], e[r+12]).
    const set = (i, a, b, c, d) => {
      const l = Math.hypot(a, b, c) || 1;
      p[i * 4] = a / l;
      p[i * 4 + 1] = b / l;
      p[i * 4 + 2] = c / l;
      p[i * 4 + 3] = d / l;
    };
    set(PLANE.RIGHT, e[3] - e[0], e[7] - e[4], e[11] - e[8], e[15] - e[12]);
    set(PLANE.LEFT, e[3] + e[0], e[7] + e[4], e[11] + e[8], e[15] + e[12]);
    set(PLANE.BOTTOM, e[3] + e[1], e[7] + e[5], e[11] + e[9], e[15] + e[13]);
    set(PLANE.TOP, e[3] - e[1], e[7] - e[5], e[11] - e[9], e[15] - e[13]);
    set(PLANE.FAR, e[3] - e[2], e[7] - e[6], e[11] - e[10], e[15] - e[14]);
    if (zeroToOne) {
      set(PLANE.NEAR, e[2], e[6], e[10], e[14]);
    } else {
      set(PLANE.NEAR, e[3] + e[2], e[7] + e[6], e[11] + e[10], e[15] + e[14]);
    }
    this.epoch++;
    return this;
  }

  /**
   * @param {number} x @param {number} y @param {number} z
   * @param {number} r Sphere radius, metres. Pass the instance's own geometry
   *   radius — NOT the batch's.
   * @returns {boolean} False only when the sphere is FULLY outside one plane,
   *   which is the conservative half of the classic false-positive trade: a
   *   sphere straddling two planes near a corner is kept. Keeping a hidden
   *   instance costs one instance; dropping a visible one is a popping bug.
   */
  sphereVisible(x, y, z, r) {
    const p = this.planes;
    for (let i = 0; i < 24; i += 4) {
      if (p[i] * x + p[i + 1] * y + p[i + 2] * z + p[i + 3] < -r) return false;
    }
    return true;
  }

  /**
   * Axis-aligned box test, for callers that hold extents rather than a radius.
   * @param {number} minX @param {number} minY @param {number} minZ
   * @param {number} maxX @param {number} maxY @param {number} maxZ
   * @returns {boolean}
   */
  boxVisible(minX, minY, minZ, maxX, maxY, maxZ) {
    const p = this.planes;
    for (let i = 0; i < 24; i += 4) {
      const nx = p[i];
      const ny = p[i + 1];
      const nz = p[i + 2];
      // The box's most-positive vertex along this normal — if even that is
      // behind the plane, every vertex is.
      const vx = nx >= 0 ? maxX : minX;
      const vy = ny >= 0 ? maxY : minY;
      const vz = nz >= 0 ? maxZ : minZ;
      if (nx * vx + ny * vy + nz * vz + p[i + 3] < 0) return false;
    }
    return true;
  }
}

/**
 * "Has the camera moved enough to be worth re-culling?"
 *
 * STATIC instances — the 1,758 rooftop units, trees and rocks — do not need a
 * fresh visibility set every frame; they need one every time the camera has
 * moved or turned enough to change the answer. This is the same shape as
 * `Traffic#_refreshCandidates`'s focus cache, and it turns a per-frame sweep
 * into an occasional one.
 *
 * ⚠ The thresholds are deliberately SMALL relative to the 38 m footprint. A
 * generous threshold saves nothing measurable (the sweep is a few thousand dot
 * products) and risks an instance popping in a frame late at the screen edge,
 * which is the one artefact this whole system must not produce.
 */
export class CameraWatch {
  /**
   * @param {Object} [o]
   * @param {number} [o.move=2] Metres of camera travel that invalidates.
   * @param {number} [o.turn=0.02] Radians of orientation change that invalidates.
   */
  constructor(o = {}) {
    const { move = 2, turn = 0.02 } = o;
    this.move2 = move * move;
    this.turn = turn;
    this._x = NaN;
    this._y = NaN;
    this._z = NaN;
    /** Forward vector, to catch a pure rotation in place. */
    this._fx = NaN;
    this._fy = NaN;
    this._fz = NaN;
  }

  /**
   * @param {number} x @param {number} y @param {number} z Camera position.
   * @param {number} fx @param {number} fy @param {number} fz Camera forward
   *   (need not be normalised — it is compared against itself).
   * @returns {boolean} True when the caller should rebuild its visible set.
   */
  changed(x, y, z, fx, fy, fz) {
    if (!(this._x === this._x)) {
      this._store(x, y, z, fx, fy, fz);
      return true;
    }
    const dx = x - this._x;
    const dy = y - this._y;
    const dz = z - this._z;
    if (dx * dx + dy * dy + dz * dz >= this.move2) {
      this._store(x, y, z, fx, fy, fz);
      return true;
    }
    // Chord length between the two forward directions ~ the angle for small
    // angles, which is the only regime that matters here.
    const ex = fx - this._fx;
    const ey = fy - this._fy;
    const ez = fz - this._fz;
    if (ex * ex + ey * ey + ez * ez >= this.turn * this.turn) {
      this._store(x, y, z, fx, fy, fz);
      return true;
    }
    return false;
  }

  /** Forces the next {@link changed} to answer true (after a teleport, a level
   *  rebuild, or anything that moved the instances rather than the camera). */
  invalidate() {
    this._x = NaN;
  }

  /** @param {number} x @param {number} y @param {number} z
   *  @param {number} fx @param {number} fy @param {number} fz */
  _store(x, y, z, fx, fy, fz) {
    this._x = x;
    this._y = y;
    this._z = z;
    this._fx = fx;
    this._fy = fy;
    this._fz = fz;
  }
}
